/**
 * CodeMirror theme built from the app's Tokyo Night CSS tokens
 * (docs/ui-design.md §6). Colors are `var(--token)` references, so the editor
 * follows `<html data-theme>` together with the rest of the app.
 */
import { HighlightStyle, syntaxHighlighting } from "@codemirror/language";
import { Compartment, type Extension } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { tags as t } from "@lezer/highlight";
import type { Theme } from "../../core/theme";

const used = new Set<string>();
const v = (token: string) => {
  used.add(token);
  return `var(--${token})`;
};

/** Syntax colors, following tokyonight.nvim's JavaScript mapping. */
const HIGHLIGHTS = [
  { tag: [t.keyword, t.controlKeyword, t.moduleKeyword, t.definitionKeyword], color: "magenta" },
  { tag: [t.operatorKeyword, t.operator, t.regexp, t.escape], color: "cyan" },
  { tag: [t.string, t.special(t.string)], color: "green" },
  { tag: [t.number, t.bool, t.null, t.atom], color: "orange" },
  { tag: [t.function(t.variableName), t.function(t.propertyName)], color: "blue" },
  { tag: [t.propertyName, t.definition(t.propertyName)], color: "teal" },
  { tag: [t.typeName, t.className, t.namespace], color: "cyan" },
  { tag: [t.variableName, t.definition(t.variableName)], color: "fg" },
  { tag: [t.self, t.special(t.variableName)], color: "red" },
  { tag: [t.punctuation, t.bracket, t.separator], color: "fg-dark" },
  { tag: [t.comment, t.lineComment, t.blockComment], color: "comment", fontStyle: "italic" },
  { tag: t.invalid, color: "red1" },
] as const;

export const tokyoNightHighlight = HighlightStyle.define(
  HIGHLIGHTS.map(({ color, ...rest }) => ({ ...rest, color: v(color) })),
);

const chrome = EditorView.theme({
  "&": {
    color: v("fg"),
    backgroundColor: v("bg"),
    fontSize: "13px",
    height: "100%",
  },
  ".cm-scroller": { fontFamily: "var(--font-mono)", lineHeight: "1.6" },
  ".cm-content": { caretColor: v("fg") },
  ".cm-cursor, .cm-dropCursor": { borderLeftColor: v("fg") },
  "&.cm-focused": { outline: "none" },
  "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection":
    { backgroundColor: v("bg-highlight") },
  ".cm-activeLine": {
    backgroundColor: `color-mix(in srgb, ${v("bg-highlight")} 50%, transparent)`,
  },
  ".cm-gutters": {
    backgroundColor: v("bg-dark"),
    color: v("comment"),
    borderRight: `1px solid ${v("border")}`,
  },
  ".cm-activeLineGutter": { backgroundColor: v("bg-highlight"), color: v("fg-dark") },
  ".cm-matchingBracket, .cm-nonmatchingBracket": {
    backgroundColor: v("bg-highlight"),
    outline: `1px solid ${v("comment")}`,
  },
  ".cm-searchMatch": { backgroundColor: `color-mix(in srgb, ${v("yellow")} 25%, transparent)` },
  ".cm-tooltip": {
    backgroundColor: v("bg-dark"),
    color: v("fg"),
    border: `1px solid ${v("border")}`,
  },
  ".cm-panels": { backgroundColor: v("bg-dark"), color: v("fg") },
});

/** Every token the editor theme references (checked against styles.css in tests). */
export const usedTokens = (): string[] => [...used];

/** CodeMirror's base styles differ for dark/light; reconfigured on theme change. */
const darkFlag = new Compartment();
const darkness = (theme: Theme) => EditorView.theme({}, { dark: theme === "dark" });

export const tokyoNight = (theme: Theme): Extension => [
  chrome,
  darkFlag.of(darkness(theme)),
  syntaxHighlighting(tokyoNightHighlight),
];

export const setEditorTheme = (view: EditorView, theme: Theme) =>
  view.dispatch({ effects: darkFlag.reconfigure(darkness(theme)) });
